import React, { useState } from 'react';
import { useAppContext } from '@cherre-frontend/core';
import {
  ConfirmationModal,
  Dialog,
  PageHeader,
  Stack,
  useConfirmationModal,
} from '@cherre-frontend/ui';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { type CustomPropertyAttribute } from '../../types/CustomPropertyAttribute';
import { useCustomPropertyAttributes } from '../../hooks/useCustomPropertyAttributes';
import { AddPropertyButton } from '../../Panels/PropertiesPanel/components/AddPropertyButton';
import { UploadPropertyButton } from '../../Panels/PropertiesPanel/components/UploadPropertyButton';
import { CustomPropertyAttributesDialog } from '../CustomPropertyAttributesDialog/CustomPropertyAttributesDialog';
import { useUpsertCustomPropertyAttributes } from './hooks/useUpsertCustomPropertyAttributes';
import { ConfigureCustomAttributesButton } from './ActionsMenuButton/ConfigureCustomAttributesButton';
import { PropertiesActionsMenuButton } from './ActionsMenuButton/PropertiesActionsMenuButton';

interface PropertiesPageHeaderButtonsProps {
  propertiesTableHasData: boolean;
}

export const PropertiesPageHeader = ({
  propertiesTableHasData,
}: PropertiesPageHeaderButtonsProps) => {
  const { showSnackbar } = useAppContext();
  const { modalProps, confirm } = useConfirmationModal();
  const [customAttributes, setCustomAttributes] = useCustomPropertyAttributes();
  const uploadPropertyFlag = useFeatureFlag('DspAdminUploadProperty');
  const configureAttributeFlag = useFeatureFlag(
    'DspConfigureCustomPropertyAttributes'
  );
  const [isCustomAttributesDialogOpen, setIsCustomAttributesDialogOpen] =
    useState(false);

  const shouldDisplayAddPropertyButton = propertiesTableHasData;
  const shouldDisplayCustomAttributesButton =
    customAttributes !== undefined && configureAttributeFlag;
  const shouldDisplayUploadButton =
    propertiesTableHasData && uploadPropertyFlag;
  const shouldDisplayActionsMenuButton =
    shouldDisplayCustomAttributesButton && shouldDisplayUploadButton;

  const openCustomAttributesDialog = () =>
    setIsCustomAttributesDialogOpen(true);
  const closeCustomAttributesDialog = () =>
    setIsCustomAttributesDialogOpen(false);

  const upsertCustomAttributes = useUpsertCustomPropertyAttributes();
  const saveCustomAttributes = async (
    attributes: CustomPropertyAttribute[]
  ) => {
    try {
      await upsertCustomAttributes(attributes);
      setCustomAttributes(attributes);
      closeCustomAttributesDialog();
      showSnackbar({
        type: 'success',
        message: 'Property attributes have been updated in the table.',
      });
    } catch (error) {
      console.error(error);
      showSnackbar({
        type: 'error',
        message: 'It was not possible to save the attributes.',
      });
    }
  };

  return (
    <PageHeader title='Properties'>
      <ConfirmationModal {...modalProps} />
      {customAttributes && (
        <Dialog
          maxWidth='md'
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              gap: 3,
              maxWidth: 710,
              padding: '20px 20px 0 20px',
              '& > *': { flex: '0 0 auto' },
            },
          }}
          open={isCustomAttributesDialogOpen}
        >
          <CustomPropertyAttributesDialog
            currentAttributes={customAttributes}
            onSave={saveCustomAttributes}
            onCancel={closeCustomAttributesDialog}
          />
        </Dialog>
      )}

      <Stack direction='row' justifyContent='flex-end' gap={2} width={'auto'}>
        {shouldDisplayAddPropertyButton && <AddPropertyButton />}

        {shouldDisplayActionsMenuButton ? (
          <PropertiesActionsMenuButton
            onBulkUpload={confirm}
            onConfigureAttributes={openCustomAttributesDialog}
          />
        ) : shouldDisplayUploadButton ? (
          <UploadPropertyButton
            mode='bulk'
            variant='button'
            confirmFunction={confirm}
          />
        ) : shouldDisplayCustomAttributesButton ? (
          <ConfigureCustomAttributesButton
            variant='button'
            onClick={openCustomAttributesDialog}
          />
        ) : null}
      </Stack>
    </PageHeader>
  );
};
